/**
 * 개발: Vite 프록시(빈 문자열).
 * 프로덕션: VITE_API_BASE_URL 비우면 동일 출처(/api, /uploads → 컨테이너 nginx → biocon-api).
 * NPM 또는 IP:port 직접 접속 모두 상대 경로로 동작합니다.
 */
const API_BASE = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ||
  ''
).replace(/\/$/, '');

/** 서버 업로드 경로(/uploads/...) 또는 외부 URL을 브라우저에서 접근 가능한 URL로 변환 */
export function resolveAssetUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
}

export type ApiResult<T> = {
  statusCode: number;
  resultCode: string;
  resultMsg: string;
  data: T;
};

async function request<T>(
  path: string,
  options: RequestInit = {},
  auth: boolean = true
): Promise<ApiResult<T>> {
  const token = auth ? localStorage.getItem('biocon_admin_token') : null;
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string>),
  };

  const url = `${API_BASE}/api/v1${path}`;

  let res: Response;
  try {
    res = await fetch(url, { ...options, headers });
  } catch {
    throw new Error('서버에 연결할 수 없습니다. API 서버가 실행 중인지 확인해주세요.');
  }

  let json: ApiResult<T>;
  try {
    json = await res.json();
  } catch {
    throw new Error(res.ok ? '응답을 처리할 수 없습니다.' : `요청 실패 (${res.status})`);
  }

  if (res.status === 401) {
    localStorage.removeItem('biocon_admin_token');
    const redirectOnAuth =
      auth &&
      path.startsWith('/admin/') &&
      !path.startsWith('/admin/auth/') &&
      !path.startsWith('/admin/me/');
    if (redirectOnAuth) {
      window.location.href = '/admin/login';
    }
    throw new Error(json.resultMsg || '인증이 필요합니다. 다시 로그인해주세요.');
  }

  if (!res.ok) {
    throw new Error(json.resultMsg || `오류가 발생했습니다. (${json.resultCode || res.status})`);
  }

  return json;
}

/** 공개 페이지용 — 관리자 토큰 미첨부, 401 시 로그인 화면으로 이동하지 않음 */
export const publicApi = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }, false),
};

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),

  /** 목록 API — query는 `?page=0&size=10` 형식 */
  getPage: <T>(path: string, query?: string) =>
    request<T>(path + (query ?? ''), { method: 'GET' }),

  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(path, { method: 'POST', body: form });
  },

  uploadWithParams: <T>(path: string, file: File, params: Record<string, string>) => {
    const form = new FormData();
    form.append('file', file);
    const query = new URLSearchParams(params).toString();
    const suffix = query ? `?${query}` : '';
    return request<T>(path + suffix, { method: 'POST', body: form });
  },

  download: async (path: string, filename: string) => {
    const token = localStorage.getItem('biocon_admin_token');
    const url = `${API_BASE}/api/v1${path}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let msg = `다운로드 실패 (${res.status})`;
      try {
        const json = await res.json();
        if (json.resultMsg) msg = json.resultMsg;
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },
};
